import type { IconProps } from '../../icons/types'
import './MarkdownEditor.css'
import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react'
import {
	Bold,
	Heading1,
	Heading2,
	Heading3,
	Indent,
	IndentDecrease,
	Italic,
	Link,
	List,
	ListOrdered,
	PaintBucket,
	Quote,
	Type,
	Underline,
} from 'lucide-react'
import { useCallback, useRef } from 'react'
import { ParagraphMarkdownIcon } from '../../icons'
import { useDebounce } from '../../utils/useDebounce'
import { Select } from '../Select'

type Font = {
	label: string
	value: string
}

const DEFAULT_FONT: Font = { label: 'DM Sans', value: '"DM Sans", sans-serif' }

const FONTS: Font[] = [
	DEFAULT_FONT,
	{ label: 'Inter', value: 'Inter, sans-serif' },
	{ label: 'Arial', value: 'Arial, sans-serif' },
	{ label: 'Georgia', value: 'Georgia, serif' },
	{ label: 'Courier New', value: '"Courier New", monospace' },
]

type BlockType = {
	label: string
	value: string
	icon: React.FC<IconProps>
	toggle: (editor: Editor) => void
	isActive: (editor: Editor) => boolean
}

const blockTypeOptions: BlockType[] = [
	{
		label: 'Heading 1',
		value: 'h1',
		icon: Heading1,
		toggle: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
		isActive: (editor: Editor) => editor.isActive('heading', { level: 1 }),
	},
	{
		label: 'Heading 2',
		value: 'h2',
		icon: Heading2,
		toggle: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
		isActive: (editor: Editor) => editor.isActive('heading', { level: 2 }),
	},
	{
		label: 'Heading 3',
		value: 'h3',
		icon: Heading3,
		toggle: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
		isActive: (editor: Editor) => editor.isActive('heading', { level: 3 }),
	},
	{
		label: 'Numbered List',
		value: 'number',
		icon: ListOrdered,
		toggle: (editor: Editor) => editor.chain().focus().toggleOrderedList().run(),
		isActive: (editor: Editor) => editor.isActive('orderedList'),
	},
	{
		label: 'Bullet List',
		value: 'bullet',
		icon: List,
		toggle: (editor: Editor) => editor.chain().focus().toggleBulletList().run(),
		isActive: (editor: Editor) => editor.isActive('bulletList'),
	},
	{
		label: 'Quote',
		value: 'quote',
		icon: Quote,
		toggle: (editor: Editor) => editor.chain().focus().toggleBlockquote().run(),
		isActive: (editor: Editor) => editor.isActive('blockquote'),
	},
	{
		label: 'Paragraph',
		value: 'paragraph',
		icon: ParagraphMarkdownIcon,
		toggle: (editor: Editor) => editor.chain().focus().setParagraph().run(),
		isActive: (editor: Editor) => editor.isActive('paragraph'),
	},
]

export function Toolbar({ editor }: { editor: Editor }) {
	const editorState = useEditorState({
		editor,
		selector: menuBarStateSelector,
	})

	const setLink = useCallback(() => {
		const previousUrl = editor.getAttributes('link').href
		const url = window.prompt('URL', previousUrl ?? 'https://')

		const isPromptCancelled = url === null
		if (isPromptCancelled) {
			return
		}

		const isPromptEmpty = url === ''
		if (isPromptEmpty) {
			editor.chain().focus().extendMarkRange('link').unsetLink().run()
			return
		}

		// update link
		editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
	}, [editor])

	return (
		<div className='toolbar'>
			<div className='m-1 w-[50px]'>
				<Select
					displaySelectedValue={false}
					selectIcon={Type}
					value={editorState.fontFamily}
					sizing='small'
					options={FONTS}
					onChange={(e) => editor.commands.setFontFamily(e)}
				/>
			</div>

			<div className='m-1 w-[50px]'>
				<Select
					value={editorState.activeBlockType?.value}
					sizing='small'
					displaySelectedValue={false}
					selectIcon={editorState.activeBlockType?.icon || ParagraphMarkdownIcon}
					options={blockTypeOptions}
					onChange={(e) => {
						// Resets to paragraph, in case of heading > quote/list switch
						editor.chain().focus().setParagraph().run()

						const blockType = blockTypeOptions.find((option) => option.value === e)
						blockType?.toggle(editor)
					}}
				/>
			</div>

			<ToolbarButton
				isActive={false}
				onClick={() => editor.chain().focus().indent().run()}
				title='Indent (Tab)'
				Icon={Indent}
			/>
			<ToolbarButton
				isActive={false}
				onClick={() => editor.chain().focus().outdent().run()}
				title='Outdent (Shift+Tab)'
				Icon={IndentDecrease}
			/>
			<ToolbarButton isActive={editorState.isBold} onClick={editorState.toggleBold} title='Bold (Ctrl+B)' Icon={Bold} />
			<ToolbarButton
				isActive={editorState.isItalic}
				onClick={editorState.toggleItalic}
				title='Italic (Ctrl+I)'
				Icon={Italic}
			/>
			<ToolbarButton
				isActive={editorState.isUnderline}
				onClick={editorState.toggleUnderline}
				title='Underline (Ctrl+U)'
				Icon={Underline}
			/>
			<Spacer />
			<ToolbarButton isActive={editorState.isLink} title='Link' Icon={Link} onClick={setLink} />
			<ColorPicker fontColor={editorState.color} applyColor={(color) => editorState.setColor(color)} />
		</div>
	)
}

function ColorPicker({ fontColor, applyColor }: { fontColor: string; applyColor: (color: string) => void }) {
	const colorRef = useRef<HTMLInputElement>(null)
	const updateColorWithDebounce = useDebounce(() => colorRef.current && applyColor(colorRef.current.value), 300)

	return (
		<>
			<ToolbarButton isActive={false} title='Color' Icon={PaintBucket} onClick={() => colorRef.current?.click()} />

			{/* Hidden color input */}
			<input
				style={{ width: 0, height: 0 }}
				type='color'
				ref={colorRef}
				defaultValue={fontColor}
				onChange={updateColorWithDebounce}
			/>
		</>
	)
}

function ToolbarButton({
	isActive,
	onClick,
	title,
	Icon,
}: {
	isActive: boolean
	onClick: (ev: React.MouseEvent<HTMLButtonElement>) => void
	title: string
	Icon: React.FC<IconProps>
}) {
	return (
		<button
			type='button'
			className={`toolbar-item ${isActive ? 'active' : ''}`}
			onMouseDown={onClick}
			title={title}
			aria-label={title}>
			<Icon size={16} />
		</button>
	)
}

function Spacer() {
	return <div className='h-8 w-px bg-neutral-200 mx-1' />
}

export function menuBarStateSelector(ctx: EditorStateSnapshot<Editor>) {
	return {
		// Text formatting
		isBold: ctx.editor.isActive('bold') ?? false,
		canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
		toggleBold: () => ctx.editor.chain().focus().toggleBold().run(),

		isItalic: ctx.editor.isActive('italic') ?? false,
		canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
		toggleItalic: () => ctx.editor.chain().focus().toggleItalic().run(),

		isUnderline: ctx.editor.isActive('underline') ?? false,
		canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
		toggleUnderline: () => ctx.editor.chain().focus().toggleUnderline().run(),

		color: ctx.editor.getAttributes('textStyle').color || '#000000',
		setColor: (color: string) => ctx.editor.chain().focus().setColor(color).run(),

		isLink: ctx.editor.isActive('link'),
		fontFamily: ctx.editor.getAttributes('textStyle').fontFamily || DEFAULT_FONT.value,

		// Block types
		activeBlockType: blockTypeOptions.find((option) => option.isActive(ctx.editor)),

		// History
		canUndo: ctx.editor.can().chain().undo().run() ?? false,
		canRedo: ctx.editor.can().chain().redo().run() ?? false,
	}
}
